import React, { useState, useCallback } from 'react'
import {
  TextField,
  TextFieldProps,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Search, Close } from '@mui/icons-material'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import useSearchForm from '@talentinc/gatsby-theme-ecom/hooks/useSearchForm'

type Props = Pick<TextFieldProps, 'onFocus' | 'onBlur' | 'autoFocus'> & {
  classes?: Partial<{
    root: string
    input: string
  }>
  onSubmit?: React.FormEventHandler<HTMLFormElement>
  onClose?: React.MouseEventHandler<HTMLButtonElement>
}

const MiniSearchForm: React.FC<Props> = ({
  onFocus,
  onBlur,
  onClose,
  onSubmit,
  autoFocus = false,
  classes: propClasses = {},
}) => {
  const { classes, cx } = useStyles()
  const [searchFocused, setSearchFocused] = useState(false)
  const { t } = useTranslation()
  const {
    searchQuery,
    htmlSearchAction,
    handleUpdateSearchQuery,
    handleSearchSubmit,
    searching,
    updateSearchQuery,
  } = useSearchForm({ defaultValue: '' })
  let endIcon = null

  const handleFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (event) => {
      setSearchFocused(true)
      onFocus?.(event)
    },
    [onFocus]
  )

  const handleBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (event) => {
      // This is needed to make pressing close on mobile close the mobile search
      // form.
      // @ts-ignore
      if (event?.relatedTarget?.id === 'close-search-form') {
        return
      }

      if (!searchQuery) {
        setSearchFocused(false)
        onBlur?.(event)
      }
    },
    [onBlur, searchQuery]
  )

  const handleSearch = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      // @ts-ignore
      document?.activeElement?.blur?.()
      handleSearchSubmit(event)
      onSubmit?.(event)
    },
    [handleSearchSubmit, onSubmit]
  )

  const handleClose = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault()
      updateSearchQuery('')
      setSearchFocused(false)
      onClose?.(event)
    },
    [updateSearchQuery, onClose]
  )

  if (searching) {
    endIcon = <CircularProgress color="inherit" size="1em" />
  } else if (searchFocused) {
    endIcon = (
      <IconButton
        onClick={handleClose}
        className={classes.closeButton}
        id="close-search-form"
        size="large"
      >
        <Close />
      </IconButton>
    )
  }

  return (
    <form
      action={htmlSearchAction}
      onSubmit={handleSearch}
      className={cx(propClasses?.root)}
    >
      <TextField
        variant="standard"
        type="search"
        name="s"
        placeholder={t(
          `miniSearchForm.placeholder${searchFocused ? 'Focused' : 'Unfocused'}`
        )}
        fullWidth
        id="blog-navigation-search"
        InputProps={{
          startAdornment: (
            <label htmlFor="blog-navigation-search">
              <Search className={classes.searchIcon} />
            </label>
          ),
          endAdornment: endIcon,
          classes: {
            input: cx(
              {
                [classes.input]: true,
                [classes.unfocusedInput]: !searchFocused,
                [classes.focusedInput]: searchFocused,
              },
              propClasses?.input
            ),
          },
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleUpdateSearchQuery}
        value={searchQuery}
        autoFocus={autoFocus}
      />
    </form>
  )
}

const useStyles = makeStyles()((theme) => ({
  searchIcon: {
    marginTop: '10px',
    color: theme.palette.primary.main,
    marginLeft: '0.8570386579em',
  },
  input: {
    [`
        &::-webkit-search-decoration,
        &::-webkit-search-cancel-button,
        &::-webkit-search-results-button,
        &::-webkit-search-results-decoration
      `]: {
      display: 'none',
    },
  },
  unfocusedInput: {
    marginLeft: '.5em',

    '&::placeholder': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      opacity: 1,
    },
  },
  focusedInput: {
    outline: 0,
    border: 0,
    width: '18.75rem',
    marginLeft: '.8em',
  },
  closeButton: {
    padding: '4.5px',
  },
  iconButton: {
    padding: '4.5px',
  },
}))

export default MiniSearchForm
